import { AbstractMesh, GPUParticleSystem, Mesh, MeshBuilder, ParticleSystem, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Geyser Behavior – periodically erupts with a warning smoke phase, then an active blast phase.
 *
 * Config fields:
 *   geyserInterval   – seconds between eruptions (default 4)
 *   geyserActiveTime – seconds the blast stays active (default 1.5)
 *   geyserForce      – upward impulse applied to player if they are above the geyser (default 20)
 *   fireHeight       – height of the blast column (default 6)
 *   fireWidth        – collision width (default 1.5)
 */
export class ObstacleGeyserBehavior extends ObstacleBehavior {
  private time: number = 0;
  private phase: 'idle' | 'warning' | 'blast' = 'idle';
  private warningDuration: number = 1.2;
  private collisionMesh: Mesh | null = null;
  private blastParticles: ParticleSystem | GPUParticleSystem | null = null;
  private smokeParticles: ParticleSystem | GPUParticleSystem | null = null;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleGeyser', startPosition, pathIndex, config);
  }

  initLevel(level: Level): ObstacleGeyserBehavior {
    super.initLevel(level);

    const fireHeight = this.config.fireHeight ?? 6;
    const fireWidth = this.config.fireWidth ?? 1.5;

    // Blast particle system (pointing up)
    this.blastParticles = this.level.particles.createFireColumnParticleSystem(this.mesh, fireHeight, 'y', 1, fireWidth);

    // Smoke warning particles – reuse a simpler system
    this.smokeParticles = this.level.particles.createImpactDustParticleSystem(this.mesh as AbstractMesh);

    // Collision box
    this.collisionMesh = MeshBuilder.CreateBox(
      'geyserCollision',
      { width: fireWidth, height: fireHeight, depth: fireWidth },
      this.level.scene
    );
    this.collisionMesh.parent = this.mesh;
    this.collisionMesh.position.y = fireHeight / 2;
    this.collisionMesh.isVisible = false;
    this.collisionMesh.isPickable = false;
    this.collisionMesh.setEnabled(false);

    this.collisionMesh.metadata = {
      playerCollision: new PlayerCollision(false, (level, player, event) => {
        if (player.vulnerable && !level.game.config.godMode) {
          const geyserForce = this.config.geyserForce ?? 20;
          // Launch the player upward (bonus launch when standing over geyser)
          player.applyUpwardForce(geyserForce);
          // Only damage if they are actually inside the blast column, not above it
          const playerY = player.getPosition().y;
          const geyserTopY = this.mesh.position.y + (this.config.fireHeight ?? 6);
          if (playerY > this.mesh.position.y && playerY < geyserTopY - 1) {
            player.collide(event, 'Blasted by geyser!');
          }
        }
      }),
    };
    this.level.player?.registerCollisionMesh(this.collisionMesh);

    return this;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    const interval = this.config.geyserInterval ?? 4;
    const activeTime = this.config.geyserActiveTime ?? 1.5;
    const cycleDuration = interval + activeTime + this.warningDuration;
    const cycleTime = this.time % cycleDuration;

    if (cycleTime < interval) {
      // Idle
      if (this.phase !== 'idle') {
        this.phase = 'idle';
        this.deactivateBlast();
      }
    } else if (cycleTime < interval + this.warningDuration) {
      // Warning smoke
      if (this.phase !== 'warning') {
        this.phase = 'warning';
        this.smokeParticles?.start();
        this.level?.game.sound.play('fire', this.mesh);
      }
    } else {
      // Blast active
      if (this.phase !== 'blast') {
        this.phase = 'blast';
        this.smokeParticles?.stop();
        this.blastParticles?.start();
        this.collisionMesh?.setEnabled(true);
        this.level?.player?.registerCollisionMesh(this.collisionMesh);
      }
    }
  }

  private deactivateBlast(): void {
    this.blastParticles?.stop();
    this.smokeParticles?.stop();
    if (this.collisionMesh) {
      this.level?.player?.unregisterCollisionMesh(this.collisionMesh);
      this.collisionMesh.setEnabled(false);
    }
  }

  detach(): void {
    this.deactivateBlast();
    this.blastParticles?.dispose();
    this.smokeParticles?.dispose();
    if (this.collisionMesh) {
      this.level?.player?.unregisterCollisionMesh(this.collisionMesh);
      this.collisionMesh.dispose();
      this.collisionMesh = null;
    }
    super.detach();
  }

  clone(): ObstacleGeyserBehavior {
    return new ObstacleGeyserBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
